import type { ReactNode } from 'react';

import { Card } from '../ui';
import styles from './EmptyState.module.css';

/**
 * The viewBox IS the Figma frame (120 × 43.04) and every rect below is that frame's
 * own number, so the mark can be re-checked against the design without a conversion.
 */
function CardStackGlyph() {
  return (
    <svg viewBox="0 0 120 43.04" width={120} height={43.04} fill="none" aria-hidden>
      <rect className={styles.back} x="12.54" y="0" width="94.93" height="38.97" rx="6.15" />
      <rect className={styles.mid} x="6.45" y="6.3" width="107.1" height="36.09" rx="6.15" />
      <rect className={styles.front} x="0" y="13.21" width="120" height="29.83" rx="6.15" />
      <rect className={styles.chip} x="80.51" y="22.68" width="27.87" height="10.06" rx="3.08" />
      <rect className={styles.line} x="11.61" y="24.23" width="58.84" height="2.32" rx="1.16" />
      <rect className={styles.line} x="11.61" y="28.88" width="48.77" height="2.32" rx="1.16" />
    </svg>
  );
}

function FileStackGlyph() {
  return (
    <svg viewBox="0 0 60 51.43" width={60} height={51.43} fill="none" aria-hidden>
      <rect className={styles.fileBack} x="6.27" y="0" width="47.46" height="46.57" rx="4" />
      <rect className={styles.mid} x="3.22" y="7.52" width="53.55" height="43.13" rx="4" />
      <rect className={styles.front} x="0" y="15.79" width="60" height="35.64" rx="4" />
      <rect className={styles.fileChip} x="4.3" y="20.09" width="23.31" height="3.7" rx="1.85" />
    </svg>
  );
}

/**
 * `surface` picks the DS Card skin by what the section sits ON, which is the rule
 * the skins encode and the one native follows: white on the grey page canvas,
 * grey on the full-bleed white Tasks band. Same split as `AgentsAtWorkTab.swift`
 * / `Workspace.kt`.
 */
export function EmptyState({
  label,
  glyph = 'card-stack',
  surface = 'sunken',
  children,
}: {
  label: string;
  glyph?: 'card-stack' | 'files';
  surface?: 'sunken' | 'stone';
  children?: ReactNode;
}) {
  return (
    <Card variant={surface} size="sm" className={styles.card}>
      <div className={styles.figure}>
        {glyph === 'files' ? <FileStackGlyph /> : <CardStackGlyph />}
        <p className={styles.label}>{label}</p>
      </div>
      {children}
    </Card>
  );
}
